import React, { useState, useMemo, useReducer } from 'react';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import rfdc from 'rfdc';

import * as Store from '@apply/store';
import {
    BackButtonWithStore,
    confirmModal,
    Question,
    QuestionType,
    ShowIf,
    alertModal,
    loginAlertModal,
    saveTemplateForm,
} from '@apply/ui';

import { Header } from '../Header';
import { useEffect } from 'react';

import { Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuthentication } from '../LoginPage';

import { Subdivision } from './Subdivision';
import { NumericFormat } from 'react-number-format';
import { LoanType } from '@apply/constants';

const formConstant = Store.FormsStore.Types.formConstant;

export interface EditFormProps {
    formType: Store.FormsStore.Types.FormType;
    currentProperty?: Store.PropertiesStore.Types.Property;
    loanType?: LoanType;
    propertyIndex?: number;
    sharedForms?: Store.FormsStore.Types.FormCollectionState;
    propertiesForms?: Store.FormsStore.Types.FormState[];
    actions?: Actions;
    dispatch?: Store.ApplyDispatchType;
    printMode?: boolean;
}

export interface Actions {
    onChangeBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangeLandBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangeFixFlipBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangeSpecSheet: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onSaveForm: (form: Store.FormsStore.Types.FormState) => void;
    loadStateFromApi?: (apiId: string, setCurrentPropertyId?: string, printToken?: string) => Promise<boolean | undefined>;
    saveStateToApi?: (apiId: string, complete: boolean, token: string) => Promise<Store.BorrowerStore.Types.IdAndToken>;
    saveSharedFormToApi?: (form: Store.FormsStore.Types.FormState) => Promise<number>;
    loadSavedApplyForms?: (apiId: string, loanType: LoanType) => Promise<Store.FormsStore.Types.FormState[] | undefined>;
}

export const getWorkingFormDataInitialState = (
    formType: Store.FormsStore.Types.FormType,
    propertiesForms: Store.FormsStore.Types.FormState[],
    currentProperty: Store.PropertiesStore.Types.Property,
    propertyGuid: string
) => {
    const propformId = (() => {
        switch (formType) {
            case formConstant.displayName.budget:
                return currentProperty?.nonRental?.budget?.formId;
            case formConstant.displayName.landBudget:
                return currentProperty?.nonRental?.landBudget?.formId;
            case formConstant.displayName.fixFlipBudget:
                return currentProperty?.nonRental?.fixFlipBudget?.formId;
            case formConstant.displayName.specSheet:
                return currentProperty?.nonRental?.specSheet?.formId;
            default:
                return undefined;
        }
    })();
    const propform = propformId ? propertiesForms.find((f) => f.guid === propformId) : undefined;
    if (propform) {
        propform['propertyGuid'] = propertyGuid;
        return rfdc()(propform);
    }
    return undefined;
};

interface DropdownOption {
    display: string;
    value: string;
}

export const EditForm: React.FC<EditFormProps> = ({
    formType,
    currentProperty,
    propertyIndex,
    propertiesForms,
    actions,
    loanType,
    printMode,
}) => {
    const navigate = useNavigate();
    const { apiId } = useParams<{ apiId: string }>();
    const { search, pathname } = useLocation();
    const params = new URLSearchParams(search);
    const propertyGuid = params.get('pid');
    const token = params.get('token');

    const { logout } = useAuthentication();

    const [propertyName, setPropertyName] = useState<string>('unknown');
    const [loading, setLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);

    const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);
    const [selectedSharedForm, setSelectedSharedForm] = useState<number>(0);
    const [complete, setComplete] = useState<boolean>(false);
    const [ignoreChangeWarning, set_ignoreChangeWarning] = useState<boolean>(false);

    const [sharedForms, setSharedForms] = useState<Store.FormsStore.Types.FormState[]>();

    const formSpecs = useMemo(() => {
        if (formType === formConstant.displayName.budget) {
            if (currentProperty?.nonRental?.generalContractorWillWork) {
                return Store.FormsStore.Types.generalContractorBudgetSpec;
            } else {
                return Store.FormsStore.Types.budgetSpec;
            }
        } else if (formType === formConstant.displayName.landBudget) {
            return Store.FormsStore.Types.landBudgetSpec;
        } else if (formType === formConstant.displayName.fixFlipBudget) {
            return Store.FormsStore.Types.fixFlipBudgetSpec;
        } else {
            return Store.FormsStore.Types.specSheetSpec;
        }
    }, [formType, currentProperty?.nonRental?.generalContractorWillWork]);

    const [workingFormData, workingFormDataDispatch] = useReducer(Store.FormsStore.formReducer, undefined);

    useEffect(() => {
        const formlist = sharedForms?.filter((f) => f.type === formType);
        if (formlist && formlist.length > 0) {
            const options: DropdownOption[] = [{ display: '', value: '' }];

            formlist.forEach((f) => {
                options.push({ display: f.name, value: f.guid });
            });

            setDropdownOptions(options);
        } else {
            setDropdownOptions([]);
        }
    }, [sharedForms, formType]);

    useEffect(() => {
        if (currentProperty && propertiesForms) {
            setPropertyName(`${propertyIndex + 1} — ${currentProperty.address.street1}`);
            const stored = getWorkingFormDataInitialState(formType, propertiesForms, currentProperty, propertyGuid);
            if (stored) {
                workingFormDataDispatch(Store.FormsStore.Actions.replaceFormState(undefined, stored));
            } else {
                const newFormState = {
                    ...Store.FormsStore.Types.formInitialState,
                    propertyGuid,
                };
                workingFormDataDispatch(
                    Store.FormsStore.Actions.replaceFormState(undefined, {
                        ...newFormState,
                        type: formType,
                        guid: uuidv4(),
                        name: `${currentProperty.address.street1} ${formType}`,
                        isDirty: false,
                        values: Store.FormsStore.Types.createEmptyFormValuesData(formType),
                    })
                );
            }
        }
    }, [currentProperty, propertyIndex, formType, propertiesForms, propertyGuid]);

    const [total, setTotal] = useState<number>(0);
    useEffect(() => {
        if (workingFormData?.sharedFormId) {
            setSelectedSharedForm(workingFormData.sharedFormId);
        }
        setTotal(Store.FormsStore.Types.getTotalAmountsFromForm(workingFormData));
    }, [workingFormData]);

    useEffect(() => {
        let canceled = false;

        if (apiId) {
            setLoading(true);

            if (loanType) {
                actions.loadSavedApplyForms(apiId, loanType).then((savedForms) => {
                    savedForms.forEach((form) => {
                        form.propertyGuid = propertyGuid;
                    });
                    setSharedForms(savedForms);
                });
            }

            actions
                .loadStateFromApi(apiId, propertyGuid, token)
                .then((complete) => {
                    if (complete) {
                        setComplete(complete);
                    }
                    workingFormDataDispatch(Store.FormsStore.Actions.updateFormIsDirty(undefined, false));
                })
                .catch((err) => {
                    /* istanbul ignore else */
                    if (!canceled) {
                        setLoadError(err);
                    }
                })
                .finally(() => {
                    /* istanbul ignore else */
                    if (!canceled) {
                        setLoading(false);
                    }
                });
        }
        return () => {
            canceled = true;
        };
    }, [apiId, propertyGuid, actions, token, loanType]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    }, [navigate, loadError, logout, pathname]);

    const handleNext = async () => {
        setSubmitting(true);

        if (
            formType === formConstant.displayName.specSheet &&
            Store.FormsStore.Types.isFormDataStructureEmpty(workingFormData.values)
        ) {
            alertModal({
                message: 'Please fill out at least one value before proceeding',
            });
            return;
        }

        if (formType === formConstant.displayName.budget && total < 75000) {
            alertModal({
                message: 'Total budget value too low, minimum total budget is $75,000',
            });
            return;
        }

        if (Store.FormsStore.Types.countOfNonCompliantValues(workingFormData) > 0) {
            alertModal({
                message: 'One or more of the fields above require attention',
            });
            return;
        }

        await handleSave();

        const base = Store.CrossStore.getPropertyRoute(formType, currentProperty);
        navigate(`/${base}/${apiId}/?pid=${currentProperty?.guid ?? ''}${token ? '&token=' + token : ''}`);
    };

    const handleSave = async () => {
        setLoading(true);

        actions.onSaveForm({
            ...workingFormData,
            sharedFormId: selectedSharedForm,
            isDirty: false,
        });
        if (formType === formConstant.displayName.budget) {
            actions.onChangeBudget(currentProperty.guid, {
                handleMode: 'editsavedform',
                formId: workingFormData.guid,
                formStorage: 'inline',
            });
        } else if (formType === formConstant.displayName.landBudget) {
            actions.onChangeLandBudget(currentProperty.guid, {
                handleMode: 'editsavedform',
                formId: workingFormData.guid,
                formStorage: 'inline',
            });
        } else if (formType === formConstant.displayName.fixFlipBudget) {
            actions.onChangeFixFlipBudget(currentProperty.guid, {
                handleMode: 'editsavedform',
                formId: workingFormData.guid,
                formStorage: 'inline',
            });
        } else if (formType === formConstant.displayName.specSheet) {
            actions.onChangeSpecSheet(currentProperty.guid, {
                handleMode: 'editsavedform',
                formId: workingFormData.guid,
                formStorage: 'inline',
            });
        }

        workingFormDataDispatch(Store.FormsStore.Actions.updateFormIsDirty(undefined, false));

        if (apiId) {
            try {
                await actions.saveStateToApi(apiId, false, token);
            } catch (err) {
                setLoadError(err);
            }
        }
        setLoading(false);
    };

    const isTemplateSaveDisabled = () => {
        const isEmpty = Store.FormsStore.Types.isFormDataStructureEmpty(workingFormData.values);
        const isNonCompliant = Store.FormsStore.Types.countOfNonCompliantValues(workingFormData) > 0;
        return isEmpty || !workingFormData.isDirty || isNonCompliant;
    };

    const SaveTemplateToApi = async (name: string, isNew: boolean) => {
        try {
            setLoading(true);
            const duplicateNameForm = sharedForms?.find(
                (f) => f.type === formType && f.name === name && f.id !== (isNew ? 0 : selectedSharedForm)
            );
            if (duplicateNameForm) {
                alertModal({
                    message: `The name "${duplicateNameForm.name}" already exists. Please provide an alternate name.`,
                });
                return;
            }

            //Update workingForm Name
            setLoading(true);
            const form = { ...workingFormData };
            form.id = isNew ? 0 : form.id;
            form.name = name;
            form.guid = null;
            form.sharedFormId = null;
            const formId = await actions.saveSharedFormToApi(form);

            //reload saved forms - then select current one.
            actions.loadSavedApplyForms(apiId, loanType).then((savedForms) => {
                savedForms.forEach((form) => {
                    form.propertyGuid = propertyGuid;
                });
                set_ignoreChangeWarning(true);
                setSharedForms(savedForms);
                setSelectedSharedForm(formId);
            });

            workingFormDataDispatch(Store.FormsStore.Actions.updateFormIsDirty(undefined, false));
        } catch (err) {
            setLoadError(err);
        } finally {
            setLoading(false);
        }

        // // some kind of timing issue comes into play here but this resolves it
        // setTimeout(() => {
        //   setLoading(false);
        // }, 0);
    };

    const handleSharedFormSave = async () => {
        if (Store.FormsStore.Types.isFormDataStructureEmpty(workingFormData.values)) {
            alertModal({
                message: 'Please fill out at least one value before saving',
            });
            return;
        }

        if (formType === formConstant.displayName.budget && total < 75000) {
            alertModal({
                message: 'Total budget value too low, minimum total budget is $75,000',
            });
            return;
        }

        if (Store.FormsStore.Types.countOfNonCompliantValues(workingFormData) > 0) {
            alertModal({
                message: 'One or more of the fields above require attention',
            });
            return;
        }

        let form = null;
        if (selectedSharedForm) {
            form = sharedForms.find((f) => f.type === formType && f.id === selectedSharedForm);
        }

        saveTemplateForm({
            title: 'Save Template',
            templateName: form?.name,
            templateId: form?.id ?? 0,
            existingTemplates: dropdownOptions,
            onConfirm: (name, isNew) => {
                SaveTemplateToApi(name, isNew);
            },
        });
    };

    const handleClear = () => {
        confirmModal({
            title: 'Please Confirm',
            message: `Confirm that you want to clear all the data on this form`,
            onConfirm: () => {
                setLoading(true);
                try {
                    setSelectedSharedForm(0);
                    const updated = {
                        ...Store.FormsStore.Types.formInitialState,
                        type: formType,
                        guid: workingFormData.guid,
                        name: workingFormData.name,
                        isDirty: true,
                        propertyGuid,
                        values: Store.FormsStore.Types.createEmptyFormValuesData(formType),
                    };
                    workingFormDataDispatch(Store.FormsStore.Actions.replaceFormState(undefined, updated));
                } catch (err) {
                    setLoadError(err);
                    setLoading(false);
                }

                // some kind of timing issue comes into play here but this resolves it
                setTimeout(() => {
                    setLoading(false);
                }, 0);
            },
        });
    };

    const onChangeSharedForm = (newvalue: string) => {
        setSelectedSharedForm(parseInt(newvalue));
    };

    const replaceDataWithShared = async (id: number) => {
        if (!sharedForms) {
            return;
        }
        const shared = sharedForms.find((f) => f.id === id && f.type === formType);
        if (shared) {
            const copy = rfdc()(shared);
            const origGuid = workingFormData.guid;
            const data = {
                ...copy,
                sharedFormId: copy.id,
                guid: origGuid,
                isDirty: true,
            };
            workingFormDataDispatch(Store.FormsStore.Actions.replaceFormState(undefined, data));
        }
    };

    useEffect(() => {
        if (selectedSharedForm) {
            const currentIsEmpty = Store.FormsStore.Types.isFormDataStructureEmpty(workingFormData.values);
            if (currentIsEmpty || ignoreChangeWarning) {
                setLoading(true);
                replaceDataWithShared(selectedSharedForm).then(() => setLoading(false));
            } else {
                confirmModal({
                    title: 'Please Confirm',
                    message: `Selecting this template will cause the current information to be lost`,
                    onConfirm: async () => {
                        setLoading(true);
                        try {
                            await replaceDataWithShared(selectedSharedForm);
                            setLoading(false);
                        } catch (err) {
                            setLoading(false);
                            setLoadError(err);
                        }
                    },
                    onCancel: () => {
                        setSelectedSharedForm(0);
                    },
                });
            }
        }
        set_ignoreChangeWarning(false);
    }, [selectedSharedForm]);

    if (!workingFormData || !currentProperty || loading) {
        return (
            <div
                className='overlay'
                data-testid='edit-form-page-loading'
            >
                <div className='overlay__inner'>
                    <div className='overlay__content'>
                        <span className='spinner'></span>
                        <span className='spinner-text'>Loading</span>
                    </div>
                </div>
            </div>
        );
    }
    if (complete && !printMode) {
        return <Navigate to={`/review/${apiId}${token ? '?token=' + token : ''}`} />;
    }
    return (
        <>
            <Header />

            <main className='edit-form-main-container'>
                <section>
                    <h2>{formType}</h2>
                    <div className='form-group'>
                        <div className='row'>
                            <div className='columns'>Property</div>
                            <div
                                className='columns'
                                style={{ textAlign: 'right' }}
                            >
                                {propertyName}
                            </div>
                        </div>
                    </div>

                    <ShowIf showIf={dropdownOptions.length > 0 && !printMode}>
                        <div className='form-group'>
                            <Question
                                type={QuestionType.RowSelector}
                                selector='savedForms'
                                styleName='ungrouped'
                                values={dropdownOptions}
                                value={selectedSharedForm}
                                onChange={onChangeSharedForm}
                            >
                                Select a Template (optional)
                            </Question>
                        </div>
                    </ShowIf>
                    <div className='row'>
                        <div
                            className='columns large-12'
                            style={{ textAlign: 'center', paddingTop: '20px' }}
                        >
                            {!loading &&
                                formSpecs.map((itm, index: number) => {
                                    const onChange = (
                                        valueName: string,
                                        newvalue: Store.FormsStore.Types.FormRecordValueType
                                    ) => {
                                        workingFormData.sharedFormId = null;
                                        workingFormDataDispatch(
                                            Store.FormsStore.Actions.updateFormValue(undefined, itm.category, valueName, newvalue)
                                        );
                                    };

                                    const values = workingFormData.values[itm.category];
                                    if (values === undefined) {
                                        return;
                                    }
                                    return (
                                        <Subdivision
                                            key={`form-sub-${index}`}
                                            category={itm.category}
                                            valueSpecs={itm.values}
                                            values={values}
                                            submitting={submitting}
                                            onChange={onChange}
                                            printMode={printMode}
                                        />
                                    );
                                })}
                            {(formType === formConstant.displayName.budget ||
                                formType === formConstant.displayName.landBudget ||
                                formType === formConstant.displayName.fixFlipBudget) && (
                                <div
                                    style={{
                                        fontSize: 'larger',
                                        width: '100%',
                                        textAlign: 'left',
                                        color: 'white',
                                        backgroundColor: '#2c5591',
                                        padding: '5px',
                                        paddingLeft: '40px',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <div>Total</div>

                                    <div style={{ paddingRight: '20px' }}>
                                        <NumericFormat
                                            displayType={'text'}
                                            value={total}
                                            thousandSeparator={true}
                                            prefix='$ '
                                            fixedDecimalScale={true}
                                            decimalScale={2}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
                <ShowIf showIf={!printMode}>
                    <div className='button-group'>
                        <BackButtonWithStore title='Return to the previous page' />
                        <button
                            className='button'
                            onClick={handleClear}
                            title='Clear all data on this form'
                        >
                            Clear
                        </button>
                        <button
                            id='saveButton'
                            className='button'
                            disabled={isTemplateSaveDisabled()}
                            onClick={handleSharedFormSave}
                            title={`Update the selected Template for re-use in later applications`}
                        >
                            Save Template
                        </button>

                        <button
                            id='nextButton'
                            className='button'
                            //disabled={workingFormData.isDirty}
                            onClick={handleNext}
                            title={`Save this ${formType} and proceed to next page`}
                        >
                            Next
                        </button>
                    </div>
                </ShowIf>
            </main>
        </>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    sharedForms: state.forms,
    propertiesForms: state.properties.forms,
    loanType: state.borrower.loanType,
    currentProperty: state.properties.items.find((prop) => prop.guid === state.properties.currentPropertyId.split('/')[0]),
    propertyIndex: state.properties.items.findIndex((prop) => prop.guid === state.properties.currentPropertyId.split('/')[0]),
});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            onChangeBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(Store.PropertiesStore.Actions.updateBudget(guid, value)),
            onChangeLandBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(Store.PropertiesStore.Actions.updateLandBudget(guid, value)),
            onChangeFixFlipBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(Store.PropertiesStore.Actions.updateFixFlipBudget(guid, value)),
            onChangeSpecSheet: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(Store.PropertiesStore.Actions.updateSpecSheet(guid, value)),
            onSaveForm: (form: Store.FormsStore.Types.FormState) => dispatch(Store.PropertiesStore.Actions.saveForm(form)),
            loadStateFromApi: (apiId: string, setCurrentPropertyId?: string, printToken?: string) =>
                dispatch(Store.CrossStore.loadStateFromApi(apiId, setCurrentPropertyId, printToken)),
            saveStateToApi: (apiId: string, complete: false, token: string) =>
                dispatch(Store.CrossStore.saveStateToApi(apiId, complete, token)),
            saveSharedFormToApi: (form: Store.FormsStore.Types.FormState) => dispatch(Store.CrossStore.saveSharedFormToApi(form)),
            loadSavedApplyForms: (apiId: string, loanType: LoanType) =>
                dispatch(Store.CrossStore.loadSavedApplyForms(apiId, loanType)),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(EditForm);
